<template>
    <card title="" :isFullPage="true">
        <data-table entity-name="enrollment" entity-name-plural="enrollments" :columns="columns" :rows="rows"
            :total-row-count="totalRowCount" selection-key="id" :action-in-progress="actionsInProgress > 0"
            :poll-interval="30"
            @queryChanged="onQueryChanged">

            <template v-slot:actions="props">
                <import-action :selection-count="props.selectionCount" :disabled="actionsInProgress > 0"
                    :entity-name="props.entityName" :entity-name-plural="props.entityNamePlural"
                    import-file-accept=".csv" :import-function="doImport"></import-action>
                <export-action v-bind="props" export-resource="enrollments"></export-action>
                <print-action  v-bind="props" @printAction="onPrint"></print-action>
                <autopilot-update-action v-bind="props" @autopilotUpdateAction="onUpdateEnrollments"></autopilot-update-action>
            </template>

            <template v-slot:cell(latest_result.status)="item">
                <div v-if="item.row.latest_result === null">
                    <span class="text-info font-weight-bold">?</span>&nbsp;<span>Never checked</span>
                </div>

                <div v-else-if="item.row.latest_result.status === 'not enrolled'">
                    <span class="text-success">✔</span>&nbsp;<span>Not enrolled</span>
                </div>
                <div v-else-if="item.row.latest_result.status === 'enrolled'">
                    <span class="text-danger">✖</span>&nbsp;<span>Enrolled</span>
                </div>
                <div v-else-if="item.row.latest_result.status === 'unknown'">
                     <span><font-awesome-icon class="text-info mr-1" icon="rotate" size="sm" spin></font-awesome-icon>Updating</span>
                </div>
                <div v-else>
                    <span class="text-info font-weight-bold">?</span>&nbsp;<span>{{ item.row.latest_result.status |
                        capitalize }}</span>
                </div>
            </template>

            <template v-slot:cell(status)="item">
                <div v-if="item.row.status === 'not enrolled'">
                    <span class="text-success">✔</span>&nbsp;<span>Not enrolled</span>
                </div>
                <div v-else-if="item.row.status === 'enrolled'">
                    <span class="text-danger">✖</span>&nbsp;<span>Enrolled</span>
                </div>
                <div v-else-if="item.row.status === 'unknown'">
                     <span><font-awesome-icon class="text-info mr-1" icon="rotate" size="sm" spin></font-awesome-icon>Updating</span>
                </div>
                <div v-else>
                    <span class="text-info font-weight-bold">?</span>&nbsp;<span>{{ item.row.status |
                        capitalize }}</span>
                </div>
            </template>
        </data-table>
    </card>
</template>
<script>
import { mapGetters } from 'vuex';
import { notify } from '../services/notify';

import api from '../services/api';
import print from '../components/DataTable/print';
import AutopilotUpdateAction from '../components/DataTable/Actions/AutopilotUpdateAction.vue'
import DataTable from '../components/DataTable/DataTable.vue';
import ExportAction from '../components/DataTable/Actions/ExportAction.vue';
import ImportAction from '../components/DataTable/Actions/ImportAction.vue';
import PrintAction from '../components/DataTable/Actions/PrintAction.vue';


export default {
    name: 'Enrollments',
    components: {
        AutopilotUpdateAction,
        DataTable,
        ExportAction,
        ImportAction,
        PrintAction,
    },
    data() {
        return {
            columns: [],
            baseColumns: [
                { key: 'created_at', type: 'datetime' },
                { key: 'latest_result.updated_at', label: 'Updated at', type: 'datetime', visible: true },
                {
                    key: 'latest_result.status', label: 'Autopilot status', visible: true, type: 'category',
                    categories: ['never checked', { key: 'unknown', label: 'Updating' }, 'not enrolled', 'enrolled', 'error']
                },
                { key: 'latest_result.message', label: 'Message' },

                { key: 'system_manufacturer', label: 'Vendor', visible: true },
                { key: 'system_product_name', label: 'Model', visible: true },
                { key: 'system_serial_number', label: 'Serial', visible: true },
                { key: 'system_uuid', label: 'UUID' },
                {
                    key: 'history', type: 'table', visible: true, columns: [
                        { key: 'status' },
                        { key: 'updated_at', type: 'datetime' },
                        { key: 'message' },
                    ],
                },
                {
                    key: 'update_status', label: 'Update', type: 'action', action: this.updateStatus,
                    actionIcon: 'retweet', visible: true, disabled: (row) => row.latest_result && row.latest_result.status === 'unknown'
                },
                {
                    key: 'extra', type: 'table', columns: [
                        { key: 'key' },
                        { key: 'value' }
                    ],
                    editable: this.onExtraUpdated,
                    visible: true
                },
            ],
            rows: [],
            totalRowCount: 0,
            lastQuery: null,
            actionsInProgress: 0,
            savedTableName: null,
        }
    },
    async created() {
        await this.$store.cache.dispatch('user/fetchMe');
        if (this.isSuperUser || this.isStaffUser || this.isPartner) {
            this.columns = [{ key: 'organization.name', label: 'Organization', visible: true }, { key: 'group.name', label: 'Group', visible: true }, ...this.baseColumns];
        }
        else if (this.isManager) {
            this.columns = [{ key: 'group.name', label: 'Group', visible: true }, ...this.baseColumns];
        }
        else {
            this.columns = this.baseColumns;
        }
    },
    methods: {
        async refreshQuery() {
            if (this.lastQuery !== null) {
                await this.onQueryChanged(this.lastQuery);
            }
        },
        async onQueryChanged(query) {
            this.lastQuery = query;
            try {
                const response = await api.getWithParams('/enrollments/', query);
                this.rows = response.data.data;
                this.totalRowCount = response.data.pagination.count;
            }
            catch (err) {
                this.rows = [];
                this.totalRowCount = 0;
                notify().error('Error fetching reports.');
            }
        },
        async doImport(files, updateCallback) {
            const importCsv = async (file) => {
                const text = await file.text();
                const lines = text.split(/\r?\n/);
                //! TODO Check the ps script - I have a feeling the header may vary!
                if (lines.length === 0 || lines[0] !== 'Manufacturer,ProductName,UUID,DeviceSerialNumber,WindowsProductID,HardwareHash,GroupTag') {
                    throw new Error('Invalid csv header');
                }
                if (lines.length < 2) {
                    throw new Error('Invalid csv body');
                }

                let enrollments = [];
                for (let i = 1; i < lines.length; ++i) {
                    const line = lines[i];
                    if (line.trim().length === 0) continue;
                    const values = [];
                    line.split(',').forEach(element => {
                        const e = element.trim();
                        values.push(e);
                    });
                    if (values.length !== 7) {
                        throw new Error('Invalid number of fields');
                    }

                    const enrollment = {
                        system_manufacturer: values[0],
                        system_product_name: values[1],
                        system_serial_number: values[3],
                        system_uuid: values[2],
                        hardware_identifier: values[5],
                    };
                    enrollments.push(enrollment);
                }
                return enrollments;
            };

            updateCallback(0, 'Preparing to upload');
            const progressMax = files.length;
            let i = 0;
            let enrollments = [];
            for (const file of files) {
                enrollments = enrollments.concat(await importCsv(file));
                i += 1;
                updateCallback((i / progressMax) * 100, `Processing file ${i} of ${progressMax}`);

            }

            updateCallback(100, 'Importing...');
            for (const enrollment of enrollments) {
                await api.postJSON('/enrollments/autopilot/', enrollment);
            }
            notify().success('Import successful.')
        },
        async onPrint(selectOrFilter, rowConverter) {
            this.actionsInProgress++;
            try {
                const response = await api.postJSON('/enrollments/select', selectOrFilter);
                const rows = response.data;
                await print.pdf(rowConverter(rows));
            }
            catch (err) {
                notify().error('Error printing a report.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
        async onUpdateEnrollments(selectOrFilter) {
            this.actionsInProgress++;
            try {
                const response = await api.put('/enrollments/autopilot/', selectOrFilter);
                await this.refreshQuery();
            }
            catch (err) {
                notify().error('Error updating enrollment status.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
        async updateStatus(row) {
            this.actionsInProgress++;
            try {
                const response = await api.put(`/enrollments/autopilot/${row.id}`);
                if (response.data.id === null) {
                    notify().error('No Autopilot license available.')
                }
                else {
                    await this.refreshQuery();
                }
            }
            catch (error) {
                notify().error('Error updating enrollment status.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
        async onExtraUpdated(rowId, data) {
            this.actionsInProgress++;
            try {
                const response = await api.put(`/enrollments/${rowId}`, data);
                await this.refreshQuery();
            }
            catch (error) {
                notify().error('Error updating extra fields.');
            }
            finally {
                this.actionsInProgress--;
            }
        },
    },
    computed: {
        ...mapGetters('user', ['isSuperUser', 'isStaffUser', 'isPartner', 'isManager']),
    },
}
</script>
<style>
.light-blue {
    color: #66B3E3;
}
</style>
